import React, { useState, useMemo } from 'react'
import { gql } from 'graphql-request'
import { useLocation } from 'react-router-dom'

import { useQuery } from '@hooks/graphql'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import Loader2 from '@/components/Loader'
import { useCurrentUser } from '@contexts/currentUser'
import InviteEducatorsModal from './InviteEducatorsModal'
import EducatorListActions from './EducatorListActions'

const EDUCATORS_QUERY = gql`
  query organization($showArchived: Boolean) {
    organization {
      id
      educators(showArchived: $showArchived) {
        role
        deactivated
        profile {
          id
          fullName
          email
        }
      }
    }
  }
`

const EducatorList = () => {
  const { user: { email: currentUserEmail } = {}, currentMembership: { role: currentUserAuthRole } } = useCurrentUser()
  const location = useLocation()

  const [showArchived, setShowArchived] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)

  const inviteEmails = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const inviteParam = params.get('invite')
    const emails = inviteParam ? inviteParam.split(',').filter(Boolean) : []
    if (emails.length > 0) {
      setShowInviteModal(true)
    }
    return emails
  }, [location.search])

  const { isLoading, data: { organization = {} } = {}, refetch } = useQuery({
    queryKey: ['educators', showArchived],
    gqlQuery: EDUCATORS_QUERY,
    variables: { showArchived }
  })

  return (
    <>
      <h2 className='text-2xl font-bold mb-4'>Educators</h2>

      <div className='flex flex-col w-full'>
        <div className='w-auto flex place-content-between mb-4 items-center'>
          <div className='flex items-center space-x-2 mr-5'>
            <Switch
              id='show-archived'
              checked={showArchived}
              onCheckedChange={() => setShowArchived((prev) => !prev)}
            />
            <Label htmlFor='show-archived'>Show archived educators</Label>
          </div>

          <Button
            onClick={() => setShowInviteModal(true)}
          >
            Invite educators
          </Button>
        </div>

        <Choose>
          <When condition={isLoading}>
            <Loader2 className='size-10 self-center mt-20' />
          </When>

          <Otherwise>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead><span className='sr-only'>Actions</span></TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                <For each='educator' of={organization.educators}>
                  <With profile={educator.profile}>
                    <TableRow key={profile.id}>
                      <TableCell>
                        {profile.fullName}
                        <If condition={educator.deactivated}>
                          <Badge variant='outline' className='ml-3 text-sm'>Archived</Badge>
                        </If>
                      </TableCell>
                      <TableCell>{profile.email}</TableCell>
                      <TableCell>{educator.role.toLowerCase()}</TableCell>
                      <TableCell className='flex items-center justify-end'>
                        <If condition={currentUserAuthRole === 'admin' && currentUserEmail !== profile.email}>
                          <EducatorListActions
                            id={profile.id}
                            email={profile.email}
                            fullName={profile.fullName}
                            role={educator.role}
                            deactivated={educator.deactivated}
                            refetch={refetch}
                          />
                        </If>
                      </TableCell>
                    </TableRow>
                  </With>
                </For>
              </TableBody>
            </Table>
          </Otherwise>
        </Choose>
      </div>

      <InviteEducatorsModal
        organizationId={organization.id}
        isOpen={showInviteModal}
        closeModal={() => setShowInviteModal(false)}
        inviteEmails={inviteEmails}
      />
    </>
  )
}

export default EducatorList
